'use client'

import { useMemo, useState } from 'react'
import type { Model, Tree } from './tree'

// القوائم المتتابعة: الصانع يملأ الموديلات، والموديل يضيّق السنوات إلى مداه.
// ولذلك هذا الجزء وحده عميل — وبقية الشريط تبقى على الخادم.
//
// ويظهر في الرئيسية فارغاً، وفي صفحة النتائج مملوءاً بما اختاره الزبون —
// فيعدّل السنة أو النظام في مكانه بدل أن يرجع إلى الرئيسية.

export type FinderInitial = {
  car_brand?: string
  car_model?: string
  year?: string
  group?: string
  q?: string
}

const field = 'h-12 rounded-sm border border-gray-300 px-3'

export function FinderForm({
  tree,
  locale,
  initial,
  heading = 'البحث اليدوي',
  note = true,
}: {
  tree: Tree
  locale: string
  initial?: FinderInitial
  heading?: string
  note?: boolean
}) {
  const [brand, setBrand] = useState(initial?.car_brand ?? '')
  const [model, setModel] = useState(initial?.car_model ?? '')

  const models = useMemo(
    () => tree.brands.find((b) => b.brand === brand)?.models ?? [],
    [tree, brand]
  )

  const years = useMemo(() => {
    const thisYear = new Date().getFullYear()
    let lo = thisYear
    let hi = 1990
    const pool: Model[] = model
      ? models.filter((m) => m.model_name === model)
      : models.length
        ? models
        : tree.brands.flatMap((b) => b.models)
    for (const m of pool) {
      if (m.year_from && m.year_from < lo) lo = m.year_from
      if (m.year_to && m.year_to > hi) hi = m.year_to
    }
    if (hi > thisYear) hi = thisYear
    if (lo < 1980) lo = 1980
    const out: number[] = []
    for (let y = hi; y >= lo; y--) out.push(y)
    return out
  }, [tree, models, model])

  // سنة مختارة خارج مدى الموديل تبقى معروضة، وإلا بدا الحقل فارغاً وهو ليس كذلك
  const selYear = initial?.year ? Number(initial.year) : null
  const yearList =
    selYear && !years.includes(selYear) ? [selYear, ...years] : years

  return (
    <form action={`/catalog`} className="rounded-sm border bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-medium">{heading}</h2>
        <p className="text-sm text-gray-500">لا تعرف رقم الهيكل؟ اختر الصانع والموديل والسنة</p>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="flex flex-col gap-1 lg:w-40">
          <label htmlFor="hf-brand" className="text-sm text-gray-600">الصانع</label>
          <select
            id="hf-brand"
            name="car_brand"
            className={field}
            value={brand}
            onChange={(e) => {
              setBrand(e.target.value)
              setModel('')
            }}
          >
            <option value="">كل الصانعين</option>
            {tree.brands.map((b) => (
              <option key={b.brand} value={b.brand}>{b.brand}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 lg:w-52">
          <label htmlFor="hf-model" className="text-sm text-gray-600">الموديل</label>
          <select
            id="hf-model"
            name="car_model"
            className={field}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={!brand}
          >
            <option value="">{brand ? 'كل الموديلات' : 'اختر الصانع أولاً'}</option>
            {models.map((m) => (
              <option key={m.model_name} value={m.model_name}>
                {m.model_name}
                {m.aliases ? ` (${m.aliases})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 lg:w-32">
          <label htmlFor="hf-year" className="text-sm text-gray-600">سنة الصنع</label>
          <select id="hf-year" name="year" className={field} defaultValue={initial?.year ?? ''}>
            <option value="">كل السنوات</option>
            {yearList.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 lg:w-48">
          <label htmlFor="hf-group" className="text-sm text-gray-600">النظام</label>
          <select id="hf-group" name="group" className={field} defaultValue={initial?.group ?? ''}>
            <option value="">كل الأنظمة</option>
            {tree.groups.map((g) => (
              <option key={g.group_name} value={g.group_name}>{g.group_name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor="hf-q" className="text-sm text-gray-600">اسم القطعة أو رقمها</label>
          <input
            id="hf-q"
            name="q"
            defaultValue={initial?.q ?? ''}
            placeholder="صبرطو محرك · فلتر زيت · 26300-35505"
            className={field}
          />
        </div>

        <button className="h-12 shrink-0 rounded-sm bg-accent px-8 font-medium text-white">
          ابحث
        </button>
      </div>

      {note && (
        <p className="mt-4 border-t pt-3 text-sm text-gray-500">
          السنة تختار جيل السيارة بمحرّكاته كلّها — فلا تحتاج معرفة رمز محرّكك.
          والبحث يتحمّل الأخطاء الإملائية وأسماء القطع المحلية وأجزاء الرقم.
        </p>
      )}
    </form>
  )
}

export default FinderForm
